/**
 * src/lib/constants/taskStatus.js — Canonical task status slugs + i18n labels.
 *
 * The data layer ALWAYS uses lowercase snake_case slugs.
 * The display layer translates via taskStatusLabel(slug, locale).
 *
 * Replaces the old STATUS_DB_TO_EN / STATUS_EN_TO_DB shim.
 */

export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'review', 'done', 'cancelled'];

const LABELS = {
  todo:        { en: 'To Do',       es: 'Por hacer' },
  in_progress: { en: 'In Progress', es: 'En progreso' },
  blocked:     { en: 'Blocked',     es: 'Bloqueado' },
  review:      { en: 'Review',      es: 'Revisión' },
  done:        { en: 'Done',        es: 'Hecho' },
  cancelled:   { en: 'Cancelled',   es: 'Cancelado' },
};

// Backwards-compat: accept legacy English/Spanish strings at the WRITE boundary
// and map them to canonical slugs. Read paths should never need this.
const LEGACY_TO_SLUG = {
  'to do':       'todo',
  'todo':        'todo',
  'not started': 'todo',
  'por hacer':   'todo',
  'in progress': 'in_progress',
  'in_progress': 'in_progress',
  'en progreso': 'in_progress',
  'blocked':     'blocked',
  'bloqueado':   'blocked',
  'en espera':   'blocked',
  'review':      'review',
  'revisión':    'review',
  'revision':    'review',
  'done':        'done',
  'hecho':       'done',
  'completada':  'done',
  'cancelled':   'cancelled',
  'canceled':    'cancelled',
  'cancelado':   'cancelled',
};

export function normalizeTaskStatus(input) {
  if (!input) return 'todo';
  const key = String(input).trim().toLowerCase();
  return LEGACY_TO_SLUG[key] || (TASK_STATUSES.includes(key) ? key : 'todo');
}

export function taskStatusLabel(slug, locale = 'en') {
  const norm = normalizeTaskStatus(slug);
  const entry = LABELS[norm];
  if (!entry) return slug;
  return entry[locale] || entry.en;
}

/** Status comparison helpers — import these instead of hardcoding strings. */
export function isDone(status) { return normalizeTaskStatus(status) === 'done'; }
export function isTodo(status) { return normalizeTaskStatus(status) === 'todo'; }
export function isBlocked(status) { return normalizeTaskStatus(status) === 'blocked'; }
export function isInProgress(status) { return normalizeTaskStatus(status) === 'in_progress'; }
export function isTerminal(status) { const s = normalizeTaskStatus(status); return s === 'done' || s === 'cancelled'; }

/**
 * Every stored spelling of a status — slug, EN/ES labels, legacy forms (lower + Title
 * case) — for SERVER-side filters (`.in('status', …)` / `not.in`) on a column that still
 * holds legacy rows. An unrecognised input is returned as-is (never silently 'todo').
 * Always pair with a normalizeTaskStatus backstop in JS: no list knows every casing.
 */
export function statusAliases(status) {
  const key = String(status ?? '').trim().toLowerCase();
  const slug = LEGACY_TO_SLUG[key] || (TASK_STATUSES.includes(key) ? key : null);
  if (!slug) return [String(status)];
  const out = new Set([slug, LABELS[slug].en, LABELS[slug].es]);
  for (const [legacy, target] of Object.entries(LEGACY_TO_SLUG)) {
    if (target === slug) { out.add(legacy); out.add(legacy.charAt(0).toUpperCase() + legacy.slice(1)); }
  }
  return [...out];
}

/** Stored spellings of the terminal statuses (done + cancelled). */
export const TERMINAL_STATUS_ALIASES = [...statusAliases('done'), ...statusAliases('cancelled')];
